import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { HandleType } from '@/enums/handle-type.enum';

export interface Connection {
  nodeId: string | null;
  handleId: string | null;
  handleSide: 'source' | 'target' | null;
  handleType: HandleType | null;
}

interface ConnectionContextValue {
  currentConnection?: Connection;
  // Like currentConnection, but its onset is delayed by COMMIT_DELAY_MS. Use this (not
  // currentConnection) to drive the "obscure incompatible nodes" dimming so a quick
  // click/double-click never paints a flash. Connection *behavior* must use currentConnection.
  obscuredConnection?: Connection;
  setCurrentConnection: (connection?: Connection) => void;
  additionalConnections: Connection[];
  setAdditionalConnections: (connections: Connection[]) => void;
}

const ConnectionContext = createContext<ConnectionContextValue>({
  currentConnection: undefined,
  obscuredConnection: undefined,
  setCurrentConnection: () => {},
  additionalConnections: [],
  setAdditionalConnections: () => {},
});

// Create a hook to use the media context
export const useConnectionContext = () => useContext(ConnectionContext);

// Delay the onset of obscuredConnection so a quick click/double-click (mousedown→up under this
// threshold) never paints the "obscure incompatible nodes" feedback. Real drags exceed it.
const COMMIT_DELAY_MS = 100;

export const ConnectionProvider = ({ children }) => {
  const [current, setCurrent] = useState<Connection | undefined>(undefined);
  const [obscured, setObscured] = useState<Connection | undefined>(undefined);
  const [additional, setAdditional] = useState<Connection[]>([]);
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
      }
    },
    [],
  );

  const setCurrentConnection = useCallback((connection?: Connection) => {
    // currentConnection drives connection behavior (auto-connect, multi-connect, suggestion menu,
    // swipe-stamp) which reads it synchronously — commit it immediately.
    setCurrent(connection);
    if (!connection) {
      setAdditional([]);
    }

    // obscuredConnection only drives visual dimming: delay its onset, clear it immediately.
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
    if (connection) {
      pendingTimerRef.current = setTimeout(() => {
        setObscured(connection);
        pendingTimerRef.current = null;
      }, COMMIT_DELAY_MS);
    } else {
      setObscured(undefined);
    }
  }, []);

  const value = useMemo<ConnectionContextValue>(
    () => ({
      currentConnection: current,
      obscuredConnection: obscured,
      setCurrentConnection,
      additionalConnections: additional,
      setAdditionalConnections: setAdditional,
    }),
    [current, obscured, additional, setCurrentConnection],
  );

  return <ConnectionContext.Provider value={value}>{children}</ConnectionContext.Provider>;
};
